import React, { useRef, useState } from 'react';
import { queuePhoto } from '../../utils/photoQueue';

// Compress an image to a JPEG Blob (max dimension + quality) before upload
function compressToBlob(file, maxDim = 1600, quality = 0.72) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Lecture du fichier impossible'));
    reader.onload = (e) => {
      const img = new Image();
      img.onerror = () => reject(new Error('Image illisible'));
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let { width, height } = img;
        if (width > maxDim || height > maxDim) {
          const ratio = Math.min(maxDim / width, maxDim / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }
        canvas.width = width;
        canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error('Compression échouée')), 'image/jpeg', quality);
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
}

function blobToDataUrl(blob) {
  return new Promise((resolve) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.readAsDataURL(blob);
  });
}

// onUpload(blob) => Promise<{ id, url }> uploads to S3; if absent, falls back to
// an ephemeral base64 dataURL (dev / no-ref).
export default function PhotoCapture({ photos = [], onAdd, onRemove, onUpload, label = 'la pièce' }) {
  const cameraRef = useRef(null);
  const [uploading, setUploading] = useState(0);
  const [error, setError] = useState('');

  const handleCapture = async (e) => {
    const files = Array.from(e.target.files || []);
    const inputEl = e.target;
    setError('');
    for (const file of files) {
      setUploading((u) => u + 1);
      try {
        const blob = await compressToBlob(file);
        const timestamp = new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
        if (onUpload) {
          try {
            const { id, url } = await onUpload(blob);
            onAdd({ id, url, caption: '', timestamp });
          } catch {
            // Hors-ligne : la photo reste sur la tablette (IndexedDB) et sera
            // envoyée automatiquement au retour du réseau
            const pendingId = await queuePhoto(blob);
            onAdd({ pendingId, src: await blobToDataUrl(blob), caption: '', timestamp });
          }
        } else {
          onAdd({ src: await blobToDataUrl(blob), caption: '', timestamp });
        }
      } catch (err) {
        setError(err.message || 'Échec de la photo');
      } finally {
        setUploading((u) => u - 1);
      }
    }
    if (inputEl) inputEl.value = '';
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <button
          type="button"
          onClick={() => cameraRef.current?.click()}
          className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold transition-colors active:bg-blue-800 min-h-[44px]"
        >
          📷 Prendre une photo
        </button>
        <label className="px-4 py-2.5 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm font-semibold text-slate-600 cursor-pointer transition-colors min-h-[44px] flex items-center">
          Importer
          <input type="file" accept="image/*" multiple onChange={handleCapture} className="hidden" />
        </label>
        {/* Hidden camera input (rear camera) */}
        <input ref={cameraRef} type="file" accept="image/*" capture="environment" onChange={handleCapture} className="hidden" />
        {uploading > 0 && (
          <span className="text-xs text-blue-600 font-semibold flex items-center gap-1.5">
            <svg className="w-3.5 h-3.5 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2v4m0 12v4m-7.07-14.93l2.83 2.83m8.48 8.48l2.83 2.83M2 12h4m12 0h4M4.93 19.07l2.83-2.83m8.48-8.48l2.83-2.83" /></svg>
            Envoi… ({uploading})
          </span>
        )}
        {photos.length > 0 && uploading === 0 && (
          <span className="text-xs text-slate-400">{photos.length} photo{photos.length > 1 ? 's' : ''}</span>
        )}
      </div>

      {error && <p className="text-red-500 text-xs mb-2">{error}</p>}

      {photos.length > 0 && (
        <div className="grid grid-cols-3 gap-2">
          {photos.map((photo, idx) => (
            <div key={photo.id || photo.pendingId || idx} className="relative group">
              {(photo.url || photo.src) ? (
                <img
                  src={photo.url || photo.src}
                  alt={photo.caption || `Photo ${idx + 1}`}
                  loading="lazy"
                  className="w-full h-28 object-cover rounded-lg border border-slate-200 bg-slate-100"
                />
              ) : (
                // Photo en attente restaurée d'une autre session : blob en
                // IndexedDB, pas d'aperçu — elle partira au retour du réseau
                <div className="w-full h-28 rounded-lg border border-amber-200 bg-amber-50 flex flex-col items-center justify-center text-amber-600 text-[10px] font-semibold gap-1">
                  <span className="text-lg">⏳</span>En attente d'envoi
                </div>
              )}
              {photo.pendingId && (photo.url || photo.src) && (
                <div className="absolute top-1 right-9 bg-amber-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded">⏳ à envoyer</div>
              )}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent rounded-b-lg px-2 py-1">
                <input
                  type="text"
                  value={photo.caption}
                  onChange={(e) => onAdd({ ...photo, caption: e.target.value }, idx)}
                  placeholder="Légende..."
                  className="w-full bg-transparent text-white text-[10px] border-none outline-none placeholder-white/50"
                />
              </div>
              <button
                onClick={() => onRemove(idx)}
                className="absolute top-1 right-1 w-7 h-7 bg-red-500 text-white rounded-full text-sm font-bold opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity flex items-center justify-center"
              >
                ×
              </button>
              {photo.timestamp && (
                <div className="absolute top-1 left-1 bg-black/50 text-white text-[9px] px-1.5 py-0.5 rounded">
                  {photo.timestamp}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
